import {
  Cog6ToothIcon,
  FolderIcon,
  HomeIcon,
  ListBulletIcon,
  BoltIcon,
  IdentificationIcon,
  WrenchScrewdriverIcon,
  MicrophoneIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { TiFlowSwitch } from "react-icons/ti";

export default useRoleAccessBasedNavigation;

const navigations = {
  dashboard: {
    name: "Dashboard",
    href: "dashboard",
    icon: <HomeIcon />,
    current: false,
  },
  assistants: {
    name: "Assistants",
    href: "assistants",
    icon: <WrenchScrewdriverIcon />,
    current: false,
  },
  knowledgebases: {
    name: "Knowledge Bases",
    href: "knowledgebases",
    icon: <FolderIcon />,
    current: false,
  },
  threads: {
    name: "Threads",
    href: "threads",
    icon: <ChatBubbleLeftRightIcon />,
    current: false,
  },
  contacts: {
    name: "Contacts",
    href: "contacts",
    icon: <IdentificationIcon />,
    current: false,
  },
  campaigns: {
    name: "Campaigns",
    href: "campaign",
    icon: <ListBulletIcon />,
    current: false,
  },
  pipelines: {
    name: "Pipelines",
    href: "pipelines",
    icon: <TiFlowSwitch className="size-4 mr-1" />,
    current: false,
  },
  integrations: {
    name: "Integrations",
    href: "integrations",
    icon: <BoltIcon />,
    current: false,
  },
  settings: {
    name: "Settings",
    href: "settings",
    icon: <Cog6ToothIcon />,
    current: false,
  },
  voiceLibrary: {
    name: "Voices Library",
    href: "voiceslibrary",
    icon: <MicrophoneIcon />,
    current: false,
  },
};

export const navigationItemsOrder = Object.keys(navigations).reduce(
  (acc, navigationItem, idx) => {
    return {
      ...acc,
      [navigationItem]: idx,
    };
  },
  {},
);

export function sortApplicationNavigationSections(applicationSections) {
  const orderedApplicationSections = applicationSections.sort(
    (a, b) => navigationItemsOrder[a] - navigationItemsOrder[b],
  );

  return orderedApplicationSections;
}

const navigationItems = Object.values(navigations);

const defaultNavigationItems = navigationItems.filter(
  (navigationItem) => navigationItem.href !== "voiceslibrary",
);

function getVisibleNavigationItems(visibleSections) {
  const orderedVisibleSections = visibleSections.sort(
    (a, b) => navigationItemsOrder[a] - navigationItemsOrder[b],
  );

  const visibleNavigationItems = orderedVisibleSections.map(
    (visibleSection) => {
      return navigations[visibleSection];
    },
  );

  return visibleNavigationItems;
}

export function isOperator(userAccessLevel) {
  return userAccessLevel === "WHITELABELOPERATOR";
}

function useRoleAccessBasedNavigation(userProfile, company) {
  const userAccessLevel = userProfile?.userAccessLevel ?? "WHITELABELVIEWER";
  const isVoiceLibraryEnabled =
    company?.voiceLibrarySettings?.elevenlabsVoicesEnabled;
    const visibleSections =
        userProfile?.applicationSectionsVisible ?? Object.keys(navigations);

  const roleAccessBasedNavigationItems = useMemo(() => {
    if (isOperator(userAccessLevel)) {
      const sections = isVoiceLibraryEnabled
        ? visibleSections
        : visibleSections.filter(
            (visibleSection) => visibleSection !== "voiceLibrary",
          );

      return getVisibleNavigationItems(sections);
    }

    if (isVoiceLibraryEnabled && !isOperator(userAccessLevel)) {
      return navigationItems;
    }

    return defaultNavigationItems;
  }, [userAccessLevel, isVoiceLibraryEnabled, visibleSections]);

  const allowedSections = useMemo(() => {
    const _allowedSections = roleAccessBasedNavigationItems.reduce(
      (acc, navigationItem) => {
        const location = navigationItem.href;

        return {
          ...acc,
          [location]: true,
        };
      },
      {},
    );

    return _allowedSections;
  }, [roleAccessBasedNavigationItems]);

  const allowedLocations = useMemo(() => {
    const _allowedLocations = roleAccessBasedNavigationItems.map((item) => {
      return item.href;
    });

    return _allowedLocations;
  }, [roleAccessBasedNavigationItems]);

  const value = useMemo(() => {
    return {
      roleAccessBasedNavigationItems,
      allowedLocations,
      allowedSections,
    };
  }, [roleAccessBasedNavigationItems, allowedLocations, allowedSections]);

  return value;
}
